import { useEffect, useRef, useState } from 'react'

import { BLANK_TRACK_ID } from '../../constants'

import type {
  UseAudioPlayerProps,
  UseSingleTrackAudioPlayerReturn,
} from './types'
import { ErrorStates } from './types'
import {
  calculateElapsedTime,
  fadeTo,
  initializeAudioContext,
  toFixedNumber,
  updateMediaSession,
} from './utils'

import type { EventTrack } from 'types/player'
import { debugLog } from 'utils/debug'

// This figure is used to detect if the player's
// playhead position is out of sync. 200ms is approximately
// what gets added when something about the memory allocation changes.
const offsetThreshold = 0.2
// Base adjustment for latency in less performant environments.
// Increased by 0.1s each tick while sync stays unstable, reset when stable.
const PLAYHEAD_ADJUSTMENT_BASE = 0
const PLAYHEAD_ADJUSTMENT_STEP = 0.1
const PLAYHEAD_ADJUSTMENT_MAX = 1.5

/**
 * Hook that keeps a single <audio> element in sync with an external clock (e.g. server time).
 *
 * Props:
 * - track: { url: string }
 * - startTime: number (epoch milliseconds, e.g. server start time)
 * - socket: object with optional getEpochTime() method returning epoch ms
 * - onPlay: function to call when the track is played
 * - onActiveFinishedAtInit: function to call when the track has finished playing when we hit play
 * - onActiveFinished: function to call when the track has finished playing
 * - onMuteToggle: function to call when the mute/unmute button is pressed
 * - onError: function to call when the track fails to play
 * - onPlayNewTrack: function to call when the track is switched to another
 *
 * Refs (all used internally; consumers only need audioRef for the <audio> element):
 * - audioRef: the <audio> DOM element
 * - audioCtxRef: Web Audio API AudioContext (created on first play)
 * - gainNodeRef: GainNode used for volume and mute/fade
 * - sourceNodeRef: MediaElementAudioSourceNode connecting the audio element to the context
 * - lastPlayedRef: last EventTrack that was playing (for switch/resync comparisons)
 * - startTimeRef: epoch start time currently used for playhead sync (can lag behind props until applied)
 * - warmupTimeoutRef: timeout ID for the 4s warmup failsafe (cleared when stable)
 * - scheduledUpdateRef: timeout ID for the coalesced track/startTime update (runs on next tick)
 * - pendingUpdateRef: { track, startTime } queued by scheduleUpdate, applied when timeout fires
 * - playheadAdjustmentRef: current seek offset (seconds) for latency compensation; ramps up when unstable
 * - instabilityCounter: number of sync ticks while playhead was out of threshold (reset when stable)
 * - stabilityCounter: number of consecutive in-sync ticks; when it reaches 2 we consider playback stable
 * - endedHandlerRef: current 'ended' listener fn so it can be removed on track switch
 * - seekAhead: true = last correction was seek-ahead, false = seek-behind (used to avoid flip-flopping)
 */
export const useSingleTrackAudioPlayer = ({
  track,
  startTime,
  socket,
  onPlay,
  onActiveFinishedAtInit,
  onActiveFinished,
  onMuteToggle,
  onError,
  onPlayNewTrack,
}: UseAudioPlayerProps): UseSingleTrackAudioPlayerReturn => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const gainNodeRef = useRef<GainNode | null>(null)
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null)
  const lastPlayedRef = useRef<EventTrack | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const warmupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduledUpdateRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingUpdateRef = useRef<{
    track: EventTrack | null
    startTime: number | null
  } | null>(null)
  const playheadAdjustmentRef = useRef(PLAYHEAD_ADJUSTMENT_BASE)
  const instabilityCounter = useRef(0)
  const stabilityCounter = useRef(0)
  const endedHandlerRef = useRef<(() => void) | null>(null)
  // By default, seek ahead first.
  const seekAhead = useRef(true)
  const [isWarmingUp, setIsWarmingUp] = useState(false)
  const [isStable, setIsStable] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [hasPlayed, setHasPlayed] = useState(false)
  const [hasAlreadyFinished, setHasAlreadyFinished] = useState(false)
  const [error, setError] = useState<ErrorStates | null>(null)

  /** Toggle mute: resume context if suspended, update state/callback, and fade gain to 0 or 0.7. */
  const handleMuteToggle = async () => {
    const ctx = audioCtxRef.current

    const gainNode = gainNodeRef.current
    if (!ctx || !gainNode) {
      return
    }
    /**
     * In case the context has paused, we force resume.
     */
    if (ctx.state === 'suspended') {
      await ctx.resume().catch(() => {
        onError?.(
          track?.integrationData.contentId ?? 'N/A',
          ErrorStates.ON_MUTE_TOGGLE,
        )
      })
    }
    const newMuted = !isMuted
    setIsMuted(newMuted)
    onMuteToggle?.(newMuted)
    fadeTo(ctx, gainNode, newMuted ? 0 : 0.7, 0.2)
  }

  /** Clear the warmup failsafe timeout so we don't get stuck in warming-up state. */
  const clearWarmupTimeout = () => {
    if (warmupTimeoutRef.current) {
      clearTimeout(warmupTimeoutRef.current)
      warmupTimeoutRef.current = null
    }
  }

  /** Enter warming-up state and set a 4s failsafe to exit it if playback never stabilizes. */
  const startWarmup = () => {
    clearWarmupTimeout()
    setIsWarmingUp(true)
    // Failsafe: don't get stuck in warmup if playback never stabilizes.
    warmupTimeoutRef.current = setTimeout(() => {
      setIsWarmingUp(false)
    }, 4000)
  }

  useEffect(() => {
    return () => {
      clearWarmupTimeout()
      if (scheduledUpdateRef.current) {
        clearTimeout(scheduledUpdateRef.current)
      }
    }
  }, [])

  /**
   * Sync playhead to expected time (from socket/startTime), optionally start playback, and mark hasPlayed.
   * If isOver is true, only corrects position and sets hasPlayed; does not play (track already finished).
   */
  const newPlayhead = async (isOver?: boolean) => {
    const audio = audioRef.current
    const ctx = audioCtxRef.current

    startWarmup()
    if (!audio || !track || !ctx) {
      clearWarmupTimeout()
      setIsWarmingUp(false)
      return
    }
    const currentTime = toFixedNumber(audio.currentTime, 3)
    const expected = toFixedNumber(
      calculateElapsedTime(socket, startTimeRef.current ?? 0),
      3,
    )
    const offset = Math.abs(expected - currentTime)
    if (offset > 0.15) {
      debugLog('Audio time offset correction', {
        'Audio element reported time': currentTime,
        'Where we expect it to be': expected,
        Offset: offset,
      })
      audio.currentTime = expected
    }
    if (!isOver) {
      try {
        await audio.play()
        if (!isMuted) {
          fadeTo(ctx, gainNodeRef.current, 0, 0)
          fadeTo(ctx, gainNodeRef.current, 0.7, 1.0)
        }
      } catch (e) {
        debugLog('Switching track, aborting play.', e)
        clearWarmupTimeout()
        setIsWarmingUp(false)
        return
      }
    }

    setHasPlayed(true)
  }

  /**
   * Playhead sync loop: runs only when hasPlayed. Waits until the next second boundary, runs tick once,
   * then runs tick every 2s. tick compares audio.currentTime to expected (from socket + startTime):
   * - If offset > offsetThreshold: seeks (ahead or behind) with playheadAdjustmentRef, ramps adjustment
   *   on repeated instability; after 10 unstable ticks fires ON_STABILITY_ERROR. Resets stabilityCounter.
   * - If in sync: increments stabilityCounter; when it reaches 2, marks stable, clears warmup, fades in
   *   gain if not muted, resets instabilityCounter and seekAhead. Cleanup clears the timeout and interval.
   */
  useEffect(() => {
    if (hasPlayed) {
      let intervalId: NodeJS.Timeout | null = null
      let timeoutId: NodeJS.Timeout | null = null

      const tick = () => {
        const audio = audioRef.current
        const audioCtx = audioCtxRef.current

        if (!audio || !track || !audioCtx) {
          return
        }

        const currentTime = toFixedNumber(audio.currentTime, 3)
        const expected = toFixedNumber(
          calculateElapsedTime(socket, startTime),
          3,
        )

        const offset = Math.abs(expected - currentTime)

        // 200ms is a good variable for 'too out of sync'
        if (offset > offsetThreshold && !hasAlreadyFinished) {
          instabilityCounter.current = instabilityCounter.current + 1

          if (instabilityCounter.current <= 10) {
            stabilityCounter.current = 0

            if (expected - currentTime < 0) {
              debugLog('Adjust/seek behind')
              if (seekAhead.current === true) {
                playheadAdjustmentRef.current = PLAYHEAD_ADJUSTMENT_BASE
                seekAhead.current = false
              } else {
                playheadAdjustmentRef.current = Math.min(
                  playheadAdjustmentRef.current + PLAYHEAD_ADJUSTMENT_STEP,
                  PLAYHEAD_ADJUSTMENT_MAX,
                )
              }
              audio.currentTime = expected - playheadAdjustmentRef.current
            } else if (expected - currentTime > 0) {
              debugLog('Adjust/seek ahead')
              if (seekAhead.current === false) {
                playheadAdjustmentRef.current = PLAYHEAD_ADJUSTMENT_BASE
                seekAhead.current = true
              } else {
                playheadAdjustmentRef.current = Math.min(
                  playheadAdjustmentRef.current + PLAYHEAD_ADJUSTMENT_STEP,
                  PLAYHEAD_ADJUSTMENT_MAX,
                )
              }
              audio.currentTime = expected + playheadAdjustmentRef.current
            } else {
              debugLog('Adjust/seek to actual approximate position')
              audio.currentTime = expected
            }
          } else {
            if (expected - currentTime < 4) {
              onError?.(
                track?.integrationData.contentId,
                ErrorStates.ON_STABILITY_ERROR,
              )
              setError(ErrorStates.ON_STABILITY_ERROR)
            }
          }
          debugLog('Set new current Time', {
            currentTime,
            expected,
            offset,
            diff: expected - currentTime,
            playheadAdjustment: playheadAdjustmentRef.current,
            instabilityCounter,
            stabilityCounter,
          })
        } else {
          debugLog('Stable player established', {
            currentTime,
            expected,
            offset,
            stabilityCounter: stabilityCounter.current,
          })
          stabilityCounter.current = stabilityCounter.current + 1
          if (stabilityCounter.current === 2) {
            instabilityCounter.current = 0
            playheadAdjustmentRef.current = PLAYHEAD_ADJUSTMENT_BASE
            seekAhead.current = true
            if (!isStable && !isMuted) {
              fadeTo(audioCtx, gainNodeRef.current, 0, 0)
              fadeTo(audioCtx, gainNodeRef.current, 0.7, 0.5)
            }
            clearWarmupTimeout()
            setIsStable(true)
            setIsWarmingUp(false)
          }
        }
      }

      // Align to the next second boundary
      const now = performance.timeOrigin + performance.now()
      const msUntilNextSecond = 1000 - (now % 1000)

      timeoutId = setTimeout(() => {
        // Run immediately on the second boundary
        tick()
        // Then set up interval to run every second
        intervalId = setInterval(tick, 1000)
      }, msUntilNextSecond)

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
        if (intervalId) {
          clearInterval(intervalId)
        }
      }
    }
  }, [hasPlayed, isStable, hasAlreadyFinished, isMuted])

  /**
   * Initial play: create AudioContext/gain/source, load track, seek to expected time, set up ended listener
   * and device-change handler, then call newPlayhead. Only runs when !hasPlayed.
   */
  const handlePlay = async () => {
    const audio = audioRef.current
    if (!audio || !track?.url) {
      return
    }

    if (!hasPlayed) {
      try {
        const initialized = await initializeAudioContext({
          current: audio,
        })

        if (!initialized) {
          return
        }
        audioCtxRef.current = initialized.ctx
        gainNodeRef.current = initialized.gain
        sourceNodeRef.current = initialized.source
        const ctx = audioCtxRef.current
        fadeTo(ctx, gainNodeRef.current, 0, 0)
        ctx.onstatechange = () => {
          if (ctx.state === 'suspended' || ctx.state !== 'running') {
            if (ctx.state !== 'closed') {
              ctx.resume()
            }
          }
        }

        // Listen for device changes and run a delay check when on is detected.
        if (window.navigator.mediaDevices) {
          window.navigator.mediaDevices.ondevicechange = () => {
            newPlayhead()
          }
        }
        audio.currentTime = 0
        startWarmup()
        if (ctx?.state === 'suspended') {
          await ctx?.resume()
        }

        // Wait for audio to load before measuring
        await new Promise<void>(resolve => {
          const onLoadedMetadata = () => {
            audio.removeEventListener('canplaythrough', onLoadedMetadata)
            resolve()
          }
          audio.addEventListener('canplaythrough', onLoadedMetadata)
          audio.src = track?.url || ''
          audio.load()
        })
        const expected = toFixedNumber(
          calculateElapsedTime(socket, startTime),
          3,
        )
        const isOver = audio.duration < expected
        if (isOver) {
          // If we're playing the placeholder, don't fire the 'already completed' event
          if (track.integrationData.contentId !== BLANK_TRACK_ID) {
            onActiveFinishedAtInit?.(track.integrationData.contentId)
          }
          setHasAlreadyFinished(true)
        }

        await audio.play()
        audio.currentTime = expected
        audio.pause()
        if (!isOver) {
          const endedHandler = () => {
            onActiveFinished?.(track.integrationData.contentId)
          }

          endedHandlerRef.current = endedHandler
          audio.addEventListener('ended', endedHandler, { once: true })
        }

        onPlay?.({
          trackId: track?.integrationData.contentId,
          trackCurrentProgress: expected,
          trackDuration: audio.duration,
          isPlaying: !isOver,
        })

        lastPlayedRef.current = track
        startTimeRef.current = startTime
        newPlayhead(isOver)
      } catch (err) {
        onError?.(track.integrationData.contentId, ErrorStates.ON_PLAY)
        setError(ErrorStates.ON_PLAY)
        console.warn('Playback failed:', err)
      }
    }
  }

  /**
   * Update start time only (same track): seek to new expected position and call newPlayhead.
   * Resets hasPlayed/hasAlreadyFinished/isStable so sync logic runs again.
   */
  const resyncStartTime = (nextTrack: EventTrack, nextStartTime: number) => {
    const audio = audioRef.current

    if (!audio || !nextTrack?.url) {
      return
    }
    const wasPlaying = !audio.paused
    if (wasPlaying || hasPlayed) {
      setHasAlreadyFinished(false)
      setHasPlayed(false)
      setIsStable(false)
      startTimeRef.current = nextStartTime
      audio.currentTime = toFixedNumber(
        calculateElapsedTime(socket, nextStartTime),
        3,
      )
      fadeTo(audioCtxRef.current, gainNodeRef.current, 0, 0)

      const expected = toFixedNumber(
        calculateElapsedTime(socket, startTime),
        3,
      )
      const isOver = audio.duration < expected
      newPlayhead(isOver)
      startTimeRef.current = nextStartTime
      debugLog('Start time change', {
        start: startTimeRef.current,
        updatedStart: nextStartTime,
        track: nextTrack,
        updatedTrack: lastPlayedRef.current,
      })
    }
  }

  /**
   * Switch to a different track: remove previous ended listener, load new src, seek to expected time,
   * set new ended listener and media session, then newPlayhead. Resets hasPlayed/hasAlreadyFinished/isStable.
   */
  const switchTrack = async (nextTrack: EventTrack, nextStartTime: number) => {
    debugLog('Switch track triggered', { nextTrack, nextStartTime })
    const _startTime = startTimeRef.current
    playheadAdjustmentRef.current = PLAYHEAD_ADJUSTMENT_BASE
    stabilityCounter.current = 0
    const audio = audioRef.current
    if (!audio || !nextTrack?.url) {
      return
    }

    // Remove previous track's ended listener so it doesn't fire for the new track
    const prevEndedHandler = endedHandlerRef.current
    if (prevEndedHandler) {
      audio.removeEventListener('ended', prevEndedHandler)
      endedHandlerRef.current = null
    }

    const wasPlaying = !audio.paused
    if (wasPlaying || hasPlayed) {
      onPlayNewTrack?.(
        lastPlayedRef.current?.integrationData.contentId || '',
        nextTrack?.integrationData.contentId,
      )
      lastPlayedRef.current = nextTrack
      startTimeRef.current = nextStartTime
      setHasAlreadyFinished(false)
      setHasPlayed(false)
      setIsStable(false)
      audio.currentTime = 0
      debugLog('Switch track to', {
        track: nextTrack,
        startTime: _startTime,
        lastPlayed: lastPlayedRef.current,
      })
      try {
        // Wait for audio to load before measuring
        await new Promise<void>(resolve => {
          const onLoadedMetadata = () => {
            audio.removeEventListener('canplaythrough', onLoadedMetadata)
            resolve()
          }
          audio.addEventListener('canplaythrough', onLoadedMetadata)

          audio.src = nextTrack?.url || ''
          audio.load()
        })
        if (!audioCtxRef.current) {
          const initialized = await initializeAudioContext({
            current: audio,
          })
          if (!initialized) {
            return
          }
          audioCtxRef.current = initialized.ctx
          gainNodeRef.current = initialized.gain
          sourceNodeRef.current = initialized.source
        }
        const expected = toFixedNumber(
          calculateElapsedTime(socket, nextStartTime),
          3,
        )
        await audio.play()
        audio.currentTime = expected
        audio.pause()
        newPlayhead()
        // Fire event finished once done.
        const endedHandler = () => {
          debugLog('Ended handler inside switch track')
          onActiveFinished?.(nextTrack.integrationData.contentId)
        }

        endedHandlerRef.current = endedHandler
        audio.addEventListener('ended', endedHandler, { once: true })

        if (nextTrack?.url) {
          updateMediaSession(nextTrack?.url)
        }
      } catch (err) {
        onError?.(
          nextTrack?.integrationData.contentId,
          ErrorStates.ON_SWITCH_TRACK,
        )
        setError(ErrorStates.ON_SWITCH_TRACK)
        console.warn('Error switching track:', err)
      }
    }
  }

  /**
   * Coalesce rapid track/startTime updates: store pending and run a single timeout(0). When it fires,
   * either switchTrack (if track id changed) or resyncStartTime (if only start time changed).
   */
  const scheduleUpdate = (
    nextTrack: EventTrack | null,
    nextStartTime: number,
  ) => {
    pendingUpdateRef.current = { track: nextTrack, startTime: nextStartTime }
    if (scheduledUpdateRef.current) {
      return
    }
    instabilityCounter.current = 0
    stabilityCounter.current = 0
    scheduledUpdateRef.current = setTimeout(() => {
      scheduledUpdateRef.current = null

      const pending = pendingUpdateRef.current
      if (!pending || !pending.track || pending.startTime === null) {
        return
      }
      const nextTrackId = pending.track.integrationData.contentId
      const lastTrackId = lastPlayedRef.current?.integrationData.contentId
      const trackChanged = nextTrackId && nextTrackId !== lastTrackId
      const startTimeChanged =
        startTimeRef.current !== null &&
        startTimeRef.current !== pending.startTime

      if (trackChanged) {
        switchTrack(pending.track, pending.startTime)
        return
      }
      if (startTimeChanged) {
        resyncStartTime(pending.track, pending.startTime)
      }
    }, 0)
  }

  // --- Coalesce rapid track/startTime updates into one effect ---
  useEffect(() => {
    scheduleUpdate(track ?? null, startTime)
  }, [track, startTime])

  return {
    audioRef,
    sourceNode: sourceNodeRef.current,
    audioCtx: audioCtxRef.current,
    gainNode: gainNodeRef.current,
    handleMuteToggle,
    handlePlay,
    hasPlayed,
    isMuted,
    isWarmingUp,
    error,
  }
}
